// FILE: src/pages/ManagerRegister.jsx

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";

/**
 * ManagerRegister
 * - Validates the invite from the URL
 * - Registers a free, read-only account_manager without tripping the paywall
 * - Links the manager to the account and marks the invite accepted
 *
 * Assumptions (adjust these if your schema differs):
 * - Invite doc path: invites/accountManagers/{inviteId}
 *   Fields: { email, accountId, invitedBy, status: 'pending'|'accepted'|'revoked' }
 * - Manager link doc path: accounts/{accountId}/managers/{managerUid}
 */
const INVITES_COLL = "invites";
const INVITES_SUB  = "accountManagers";

export default function ManagerRegister() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [state, setState] = useState({
    loading: true,
    error: "",
    invite: null,
    email: "",
    password: "",
    working: false,
  });

  const inviteId = useMemo(() => {
    return params.get("inviteId") || params.get("invite");
  }, [params]);

  useEffect(() => {
    (async () => {
      try {
        if (!inviteId) throw new Error("Missing invite.");
        const snap = await getDoc(doc(db, INVITES_COLL, INVITES_SUB, inviteId));
        if (!snap.exists()) throw new Error("Invite not found.");
        const invite = snap.data();
        if (invite.status && invite.status !== "pending") {
          throw new Error("This invite is no longer valid.");
        }
        setState((s) => ({
          ...s,
          loading: false,
          invite,
          email: invite.email ?? "",
        }));
      } catch (e) {
        setState((s) => ({ ...s, loading: false, error: e.message || "Invalid invite." }));
      }
    })();
  }, [inviteId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!state.invite) return;
    setState((s) => ({ ...s, working: true, error: "" }));
    try {
      // ensure clean auth state
      try { await signOut(auth); } catch (_) {}

      const cred = await createUserWithEmailAndPassword(auth, state.email, state.password);
      const uid = cred.user.uid;
      const { accountId, invitedBy, email } = state.invite;

      const batch = writeBatch(db);

      // user profile
      batch.set(doc(db, "users", uid), {
        email,
        role: "account_manager",
        managedAccountId: accountId,
        accessEnabled: false, // start locked
        invitedBy: invitedBy || null,
        createdAt: serverTimestamp(),
      }, { merge: true });

      // link under the account
      batch.set(doc(db, "accounts", accountId, "managers", uid), {
        email,
        accessEnabled: false,
        invitedBy: invitedBy || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // mark invite accepted
      batch.set(doc(db, INVITES_COLL, INVITES_SUB, inviteId), {
        status: "accepted",
        acceptedAt: serverTimestamp(),
        acceptedBy: uid,
      }, { merge: true });

      await batch.commit();

      navigate("/manager?accepted=1", { replace: true });
    } catch (e) {
      setState((s) => ({ ...s, working: false, error: e.message || "Registration failed." }));
      return;
    }
  }

  if (state.loading) return null;
  if (state.error) {
    return (
      <div className="p-6 max-w-lg mx-auto">
        <h1 className="text-xl font-semibold mb-2">Invitation problem</h1>
        <p className="text-red-600">{state.error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-lg mx-auto">
      <h1 className="text-2xl font-bold mb-4">Set your password</h1>
      <p className="mb-6">
        You’re accepting read-only access to a business account as a Business Account Manager. You won’t be billed.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm mb-1">Email</label>
          <input
            type="email"
            value={state.email}
            disabled
            className="w-full border rounded p-2 bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm mb-1">Password</label>
          <input
            required
            minLength={8}
            type="password"
            value={state.password}
            onChange={(e) => setState((s) => ({ ...s, password: e.target.value }))}
            className="w-full border rounded p-2"
            placeholder="Create a secure password"
          />
        </div>

        <button
          disabled={state.working || state.password.length < 8}
          className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
        >
          {state.working ? "Creating..." : "Create account"}
        </button>

        {!!state.error && <p className="text-red-600">{state.error}</p>}
      </form>
    </div>
  );
}
